/**
 * The kit's settings contract (`NxSettingsSource` of `@blpde/nx-ui`) on the
 * account config path (I-2510). The kit never imports the shell: it reads and
 * writes keys through the source `main.ts` provides app-wide, and this source
 * maps each key onto `useAccountConfig` — the shared store per key, the boot
 * cache for the first paint, debounced merge patches onto the session's
 * profile layer. So an NxTable's zebra (`table`), an NxWorkspace's widths
 * (`shell.panes.<storageKey>`) and a grid's filter sets (`erp.grid.<key>`)
 * follow the account like every other setting.
 */
import { computed, type Ref } from "vue";
import type { NxSettingsDocument, NxSettingsSource } from "@blpde/nx-ui";
import { useAccountConfig, type JsonObject } from "@blpde/connect";

export function createAccountSettingsSource(): NxSettingsSource {
  return {
    get<T extends object = NxSettingsDocument>(key: string) {
      // Every read goes through `useAccountConfig`: it loads the key when it
      // is not loaded yet (a handle made before the login would not), and
      // hands out the one shared store otherwise.
      return useAccountConfig<JsonObject>(key).effective as unknown as Readonly<
        Ref<T | undefined>
      >;
    },
    patch(key: string, partial: NxSettingsDocument) {
      void useAccountConfig<JsonObject>(key, { immediate: false }).patch(
        partial,
      );
    },
    // The fields a tenant or company locked (master's `enforced`).
    locked(key: string) {
      const handle = useAccountConfig<JsonObject>(key);
      return computed(() => [
        ...new Set(handle.enforced.value.map((item) => item.field)),
      ]);
    },
  };
}
